import { call, put, take } from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import { routes } from "../../common/api/routes";
import { postAiAgentChatStream } from "../../common/api/aiChatStream";
import { CHAT_API_CONTEXT_LIMIT } from "../../common/chatHistory";
import {
  getCompanions,
  getCompanionsError,
  getCompanionsSuccess,
  getChatCompanionsSuccess,
  getChatCompanionsError,
  addNewMsgCHat,
  getChatHistorySuccess,
  getChatHistoryError,
  sendMsgCompanions,
  updateChatFeedbackSuccess,
  aiCompanionsChatStreamStart,
  aiCompanionsChatStreamDelta,
  aiCompanionsChatStreamDone,
} from "../actions";
import { clearMsgCompanions, getCompanionsSingleSuccess } from "../actions/aiCompanions";

function chatStreamChannel({ id, messages }) {
  return eventChannel((emit) => {
    const controller = new AbortController();
    postAiAgentChatStream({
      id,
      messages,
      signal: controller.signal,
      onDelta: (_chunk, full) => emit({ type: "delta", full }),
    })
      .then((result) => {
        emit({ type: "done", result });
        emit(END);
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          emit(END);
          return;
        }
        emit({ type: "error", error });
        emit(END);
      });
    return () => controller.abort();
  });
}

export function* getaiCompanionsSaga(params) {
  yield put(getCompanions(params.data));
  try {
    const response = yield call(routes.getAiAgents, params.data);
    yield put(getCompanionsSuccess(response));
    if(params.data?.name__iexact?.length){
      yield put(getCompanionsSingleSuccess(response?.results[0]?.id));
    }
  } catch (error) {
    yield put(getCompanionsError(error));
  }
}
export function* getChatHistorySaga(params) {
  yield put(clearMsgCompanions());
  try {
    const response = yield call(routes.getAiAgentsChatHistory, params.payload);
    const data = response?.filter(item => item.role !== 'system').map(item => {
      return {
        text: item?.content || item?.message,
        type: (item?.sender || item?.role) === "user" ? 'user' : 'assistant',
        date: item?.timestamp,
        message_id: item?.message_id,
        feedback: item?.feedback,
      }
    })
    yield put(getChatHistorySuccess(groupMessagesByDate(data)));
    if (!params.payload?.meta_title && !data?.length) {
      yield put(sendMsgCompanions({ id: params.payload?.id, text: 'Hi', first_msg: params.payload?.first_msg, role: 'user', hitFirstTime: true, history: [] }));
    }
  } catch (error) {
    yield put(getChatHistoryError(error));
    if (!params.payload?.meta_title) {
      yield put(sendMsgCompanions({ id: params.payload?.id, text: 'Hi', first_msg: params.payload?.first_msg, role: 'user', hitFirstTime: true, history: [] }));
    }
  }
}
export function* sendChatMsgAi(params) {
  yield put(addNewMsgCHat(params.payload));
  const history = Array.isArray(params.payload.history)
    ? params.payload.history.slice(-CHAT_API_CONTEXT_LIMIT)
    : [];
  const messages = [
    { role: "user", content: params.payload.first_msg },
    ...history,
    { content: params.payload.text, role: params.payload.role },
  ];
  const useStream = process.env.REACT_APP_AI_CHAT_STREAM !== "false";

  if (!useStream) {
    const param = { ...params.payload, history };
    try {
      const response = yield call(routes.getAiAgentsChat, param);
      let data = response?.response?.map(item => {
        const msg = item.message || {};
        return {
          text: msg.content,
          type: msg.role,
          date: new Date(),
          message_id: msg.message_id,
          feedback: msg.feedback,
        }
      })
      yield put(getChatCompanionsSuccess(data));
    } catch (error) {
      yield put(getChatCompanionsError('Something went wrong.'));
    }
    return;
  }

  yield put(aiCompanionsChatStreamStart());
  const chan = yield call(chatStreamChannel, {
    id: params.payload.id,
    messages,
  });
  try {
    while (true) {
      const ev = yield take(chan);
      if (ev === END) {
        break;
      }
      if (ev.type === "delta") {
        yield put(aiCompanionsChatStreamDelta(ev.full));
      } else if (ev.type === "done") {
        const r = ev.result;
        yield put(
          aiCompanionsChatStreamDone({
            text: r.fullText,
            message_id: r.messageId,
          })
        );
        break;
      } else if (ev.type === "error") {
        throw ev.error;
      }
    }
  } catch (error) {
    yield put(getChatCompanionsError("Something went wrong."));
  } finally {
    chan.close();
  }
}

export function* updateChatFeedbackSaga(params) {
  try {
    yield call(routes.updateAiAgentsChatFeedback, params.payload);
    yield put(updateChatFeedbackSuccess({ message_id: params.payload.message_id, feedback: params.payload.feedback }));
  } catch (error) {
    // Optionally dispatch error; UI can keep previous state
  }
}

function groupMessagesByDate(messages) {
  // Sort messages by date
  messages.sort((a, b) => new Date(a.date) - new Date(b.date));

  let groupedMessages = [];
  let lastDate = null;

  messages.forEach(message => {
    let messageDate = new Date(message.date).toISOString().split('T')[0]; // Extract YYYY-MM-DD
    if (lastDate !== messageDate) {
      groupedMessages.push({ type: "date", text: formatDate(message.date) });
      lastDate = messageDate;
    }

    groupedMessages.push(message);
  });

  return groupedMessages;
}
const formatDate = (timestamp) => {
  const now = timestamp ? new Date(timestamp) : new Date();
  const date = now.toLocaleDateString("en-US", {
    weekday: "long", // Monday
    year: "numeric", // 2025
    month: "long", // February
    day: "numeric", // 25
  });
  const time = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // 3:44 PM format
  });
  return `${date} | ${time}`;
};
